import { defineStore } from 'pinia'
import { http, axiosErrorHandler } from '@/services/api'
import jwt_decode from 'jwt-decode'
import * as Sentry from '@sentry/vue'
import type {
  APIResponse,
  Client_Auth_Response,
  Client_Refresh_Response,
  ClientData,
  JWTPayload,
} from '@/services/api/types'
import { useCoreSocket } from '@/services/socket/core-socket'
import i18n from '@/i18n'
import {
  areLanguagesEquivalent,
  normalizeAvailableLanguages,
  resolveAllowedLanguage,
  type SupportedWidgetLanguage,
  SUPPORTED_WIDGET_LANGUAGES,
  validateLanguage,
} from '@/helpers/shared'
import { applyClientPrimaryColor } from '@/helpers/client-theme'

const SCOPED_LANGUAGE_STORAGE_KEY = 'core-widget-language-overrides'

type ScopedLanguageMap = Record<string, SupportedWidgetLanguage>

function readScopedLanguageMap(): ScopedLanguageMap {
  try {
    const raw = localStorage.getItem(SCOPED_LANGUAGE_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Record<string, string>
    const sanitized: ScopedLanguageMap = {}
    Object.entries(parsed).forEach(([scope, language]) => {
      const normalized = validateLanguage(language)
      if (scope && normalized) {
        sanitized[scope] = normalized as SupportedWidgetLanguage
      }
    })
    return sanitized
  } catch {
    return {}
  }
}

function writeScopedLanguageMap(map: ScopedLanguageMap) {
  localStorage.setItem(SCOPED_LANGUAGE_STORAGE_KEY, JSON.stringify(map))
}

function buildLanguageScope(data: ClientData, userId: string | null | undefined): string {
  const normalizedUserId = userId?.trim() || '__none__'
  return [data.uuid, data.requestType, normalizedUserId].join('|')
}

interface User {
  origin: string
  name: string
}

export interface State {
  user: User | null
  clientQuery: ClientData | null
  clientFullPath: string | null
  refreshToken: string | null
  apiKey: string | null
  authenticated: boolean
  standalone?: boolean
  beta_popup_message: string | null
  feedback_url?: string | null
  footer_wiki_button_enabled: boolean
  footer_wiki_button_label?: string | null
  footer_wiki_button_url?: string | null
  available_languages: SupportedWidgetLanguage[]
  show_submit_project_data: boolean
  submit_project_data_text?: string | null
  submit_project_data_text_translations?: Record<string, string> | null
  submit_project_data_file_url?: string | null
  activeMetaUserId: string | null
}

export const useClientStore = defineStore('client', {
  state: (): State => ({
    user: null,
    clientQuery: null,
    clientFullPath: null,
    refreshToken: null,
    apiKey: null,
    authenticated: false,
    standalone: false, // local development
    beta_popup_message: null,
    feedback_url: null,
    footer_wiki_button_enabled: false,
    footer_wiki_button_label: 'Wiki',
    footer_wiki_button_url: null,
    available_languages: [...SUPPORTED_WIDGET_LANGUAGES],
    show_submit_project_data: false,
    submit_project_data_text: null,
    submit_project_data_text_translations: null,
    submit_project_data_file_url: null,
    activeMetaUserId: null,
  }),
  getters: {
    getUser: (state): User | null => state.user || null,
    getClientQuery: (state): ClientData | null => state.clientQuery || null,
  },
  actions: {
    setClientReferrer(fullPath: string, referrer: string) {
      localStorage.setItem('core-widget-referrer', referrer)
      localStorage.setItem('core-widget-fullpath', fullPath)
      this.$patch({ clientFullPath: fullPath })
    },
    setClientQuery(data: ClientData | null, widgetType: string | null = null) {
      if (data) {
        this.$patch({ clientQuery: data, activeMetaUserId: null })
      } else {
        // standalone widget - @important only for development standalone widget!
        this.$patch({
          clientQuery: {
            uuid: 'fa2e068d-7561-4c1f-af07-2a6505a7f5ee', // Standalone client
            // uuid: '637731ed-1626-405a-8e6c-46c00cae32e2', // LU client
            origin: window.location.origin,
            language: 'de-CH',
            requestType: widgetType || 'project_architect',
            standalone: true,
            beta_popup_message:
              '## TEST Beta Notification\n\nThis is a **beta** version of the widget. Please provide feedback!',
          } as ClientData,
        })
        this.$patch({ clientFullPath: 'standalone', activeMetaUserId: null })
      }
    },
    async initClient(data: ClientData) {
      const coreSocket = useCoreSocket()
      const manualLanguage = this.getManualLanguagePreference(data, this.activeMetaUserId, this.available_languages)
      const dataLanguage = validateLanguage(data.language)
      const preferredLanguage = manualLanguage || dataLanguage
      let url = `/client-auth?key=${data.uuid}&origin=${data.origin}&lang=${preferredLanguage}&type=${data.requestType}`

      if (data.nonce && data.message) {
        url += '&nonce=' + data.nonce + '&message=' + data.message
      }

      let error = null
      try {
        const response: APIResponse<Client_Auth_Response> = await http.get(url)
        applyClientPrimaryColor(
          (response.data as Client_Auth_Response & { primary_color?: string | null }).primary_color
        )
        const readyState: number = await coreSocket.initCoreSocket(response.data?.jwt)
        Sentry.logger.debug('Socket ready state', { readyState })
        if (readyState !== 1) {
          error = axiosErrorHandler('socket_init')
          Sentry.captureException(new Error('Socket init failed: No connection'))
        } else {
          const availableLanguages = normalizeAvailableLanguages(response.data.available_languages)
          const payload = jwt_decode(response.data.jwt) as JWTPayload
          const jwtLanguage = payload.language || payload.defaultLanguage
          const languageToUse = resolveAllowedLanguage(
            manualLanguage || dataLanguage || jwtLanguage,
            availableLanguages,
            payload.defaultLanguage
          )

          i18n.global.locale.value = languageToUse
          this.$patch({
            user: {
              origin: payload.origin,
              name: payload.name,
            },
            refreshToken: response.data.refreshToken,
            apiKey: payload.sub,
            authenticated: true,
            standalone: data?.standalone || false,
            beta_popup_message: response.data.beta_popup_message || null,
            feedback_url: response.data.feedback_url || null,
            footer_wiki_button_enabled: response.data.footer_wiki_button_enabled || false,
            footer_wiki_button_label: response.data.footer_wiki_button_label || 'Wiki',
            footer_wiki_button_url: response.data.footer_wiki_button_url || null,
            available_languages: availableLanguages,
            show_submit_project_data: response.data.show_submit_project_data || false,
            submit_project_data_text: response.data.submit_project_data_text || null,
            submit_project_data_text_translations: response.data.submit_project_data_text_translations || null,
            submit_project_data_file_url: response.data.submit_project_data_file_url || null,
          })

          Sentry.setUser({
            id: payload.sub,
            username: payload.name,
          })
          Sentry.setTag('client_uuid', data.uuid)
          Sentry.setTag('request_type', data.requestType)
          Sentry.setTag('standalone', String(data.standalone || false))

          if (
            manualLanguage &&
            availableLanguages.includes(manualLanguage as SupportedWidgetLanguage) &&
            !areLanguagesEquivalent(manualLanguage, jwtLanguage) &&
            coreSocket.messageCenter
          ) {
            coreSocket.messageCenter.triggerMessage('set_language', { language: manualLanguage })
            coreSocket.messageCenter.refetchLocalizedContent()
          }

          coreSocket.setRefreshToken(response.data.refreshToken)
          this.doRefreshToken() // refresh it in 60 minutes - it is valid for 65 minutes
        }
      } catch (err: unknown) {
        Sentry.captureException(err)
        error = axiosErrorHandler(err)
      }

      if (!this.authenticated) {
        if (document?.referrer) {
          window.parent.postMessage({ type: 'init_fail', error: 'Could not connect with client' }, document.referrer)
        }
        return error // send error to guard
      }
    },

    doRefreshToken(timeout?: number) {
      setTimeout(
        async () => {
          try {
            const url = '/client-auth/refresh?refreshToken=' + this.$state?.refreshToken
            const response: APIResponse<Client_Refresh_Response> = await http.get(url)
            this.$patch({ refreshToken: response.data.refreshToken })
            useCoreSocket().setRefreshToken(response.data.refreshToken)
            this.doRefreshToken() // refresh in 60 minutes - it is valid for 4h
            Sentry.logger.debug('Refresh token renewed successfully')
          } catch (err) {
            Sentry.captureException(err)
            /* 401: refreshToken has expired - can happen in safari with document.hidden */
            if ((err as any)?.response?.status == 401 || (timeout && timeout > 60 * 1000)) {
              Sentry.logger.error('Refresh token cancelled - expired or too many timeouts')
              useCoreSocket().setRefreshToken(null)
              return
            }
            const nextTimout = timeout ? (timeout += timeout) : 1000
            this.doRefreshToken(nextTimout) // try to refresh on Error;
          }
        },
        timeout || 60 * 60 * 1000
      )
    },
    getManualLanguagePreference(
      data: ClientData,
      userId?: string | null,
      availableLanguages?: SupportedWidgetLanguage[]
    ): SupportedWidgetLanguage | '' {
      const scopeWithUser = buildLanguageScope(data, userId)
      const scopedMap = readScopedLanguageMap()
      const storedLanguage = scopedMap[scopeWithUser]
      const resolvedAvailableLanguages = availableLanguages ?? this.available_languages

      if (!storedLanguage || !resolvedAvailableLanguages.includes(storedLanguage)) {
        return ''
      }

      return storedLanguage
    },
    setManualLanguagePreference(language: SupportedWidgetLanguage, userId?: string | null) {
      if (!this.clientQuery || !this.available_languages.includes(language)) {
        return
      }
      const scope = buildLanguageScope(this.clientQuery, userId ?? this.activeMetaUserId)
      const scopedMap = readScopedLanguageMap()
      scopedMap[scope] = language
      writeScopedLanguageMap(scopedMap)
    },
    registerMetaUserId(userId: string | null) {
      this.$patch({ activeMetaUserId: userId || null })

      if (!this.clientQuery || !userId) {
        return
      }

      const manualLanguage = this.getManualLanguagePreference(this.clientQuery, userId, this.available_languages)
      if (!manualLanguage || areLanguagesEquivalent(manualLanguage, String(i18n.global.locale.value))) {
        return
      }

      i18n.global.locale.value = manualLanguage
      const coreSocket = useCoreSocket()
      if (coreSocket.messageCenter) {
        coreSocket.messageCenter.triggerMessage('set_language', { language: manualLanguage })
        coreSocket.messageCenter.refetchLocalizedContent()
      }
    },
  },
})
